import { useTranslation } from "react-i18next";

import Icons from "@/components/icons";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { formatDuration } from "@/lib/utils/dateUtils";
import { formatCurrency } from "@/utils/valueFormat";

interface CampaignJobBoardMetricsProps {
  startDate?: string | null;
  endDate?: string | null;
  monthlyBudget?: number | null;
  isLoading?: boolean;
}

function CampaignJobBoardMetrics({
  startDate,
  endDate,
  monthlyBudget,
  isLoading = false,
}: CampaignJobBoardMetricsProps) {
  const { t, i18n } = useTranslation();

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const durationText = formatDuration(startDate, endDate, i18n.language);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
      <MetricCard
        icon={
          <Icons.CalendarIcon className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />
        }
        label={t("common.startDate")}
        value={formatDate(startDate)}
        variant="gradient"
        isLoading={isLoading}
      />

      <MetricCard
        icon={
          <Icons.CalendarIcon className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />
        }
        label={t("common.endDate")}
        value={formatDate(endDate)}
        variant="gradient"
        isLoading={isLoading}
      />

      <MetricCard
        icon={<Icons.EuroIcon className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />}
        label={`${t("common.monthly")} ${t("common.budget")}`}
        value={
          monthlyBudget != null ? formatCurrency(monthlyBudget) : "-"
        }
        variant="gradient"
        isLoading={isLoading}
      />

      <MetricCard
        icon={
          <Icons.ScheduleIcon className="h-4 w-4 lg:h-5 lg:w-5 text-primary" />
        }
        label={t("common.duration", { defaultValue: "Duration" })}
        value={durationText || "-"}
        variant="gradient"
        isLoading={isLoading}
      />
    </div>
  );
}

export default CampaignJobBoardMetrics;
